import * as React from 'react';

import Button from '@common/react/components/Forms/Button';
import { RequestButton } from '@common/react/components/UI/LoadingButton/LoadingButton';

import { UserGroupType } from '@commonTuna/react/objects/UserGroup';

import {
	AppointmentInsurance,
	AppointmentInsuranceStatusesInfo,
} from '@app/objects/AppointmentInsurance';
import { Appointment } from '@app/objects/Appointment';
import { Links } from '@app/components/UI/Links/Links';
import { useLinkBuilder } from '@app/hooks/useLinkBuilder';
import UserRoleAccess from '@app/components/UI/UserRoleAccess/UserRoleAccess';
import { useUserGroupContext } from '@app/components/UI/UserGroupProvider/UserGroupProvider';

interface PropsWithAppointment {
	appointment: Appointment;
	className?: string;
	asButton?: boolean;
}

interface DisabledLayoutProps {
	className: string;
	asButton: boolean;
}

const DisabledLayout: React.FC<DisabledLayoutProps> = ({ className, asButton }) => {
	return asButton
		? <Button disabled className={className}><i className="fa fa-medkit" /></Button>
		: (
			<span title="Appointment Insurance Coding" className={`${className} text-muted`}>
				<i className="fa fa-medkit" />
			</span>
		);
};

interface ActiveLayoutProps {
	className: string;
	appointment: Appointment;
}

const ActiveLayout: React.FC<ActiveLayoutProps> = (props) => {
	const { appointment, className } = props;

	const linkBuilder = useLinkBuilder();

	const hasInsurance = appointment.insurance || appointment.location?.allowAutoCoding;
	const styleIcon = hasInsurance && appointment.appointmentInsurance
		? { color: AppointmentInsuranceStatusesInfo[appointment.appointmentInsurance.status].color }
		: { color: 'grey' };
	const caption = hasInsurance && appointment.appointmentInsurance
		? AppointmentInsuranceStatusesInfo[appointment.appointmentInsurance.status].name
		: 'No insurance';

	if (appointment.appointmentInsuranceId) {
		return (
			<Links.Insurance id={appointment.appointmentInsuranceId} className={className} title={caption}>
				<i className="fa fa-medkit" style={styleIcon} />
			</Links.Insurance>
		);
	}

	return (
		<RequestButton<AppointmentInsurance>
			requestType="getAppointmentInsuranceId"
			requestProps={{
				appointmentId: appointment.id,
			}}
			onSuccess={({ res }) => linkBuilder.insurance(res.id).navigate()}
			title={caption}
			className={`${className}`}
		>
			<i className="fa fa-medkit" style={styleIcon} />
		</RequestButton>
	);
};

export const LinkToInsuranceEditor: React.FC<PropsWithAppointment> = (props) => {
	const {
		appointment,
		asButton = true,
		className = 'btn btn-sm btn-default',
	} = props;

	const { checkUserGroupAccess } = useUserGroupContext();

	const hasInsurance = appointment.insurance || appointment.location?.allowAutoCoding;

	return <UserRoleAccess roles={[]} or={checkUserGroupAccess(UserGroupType.InsuranceAccess)}>
		{hasInsurance
			? <ActiveLayout appointment={appointment} className={`${asButton ? '' : 'btn-background'} ${className}`} />
			: <DisabledLayout className={className} asButton={asButton} />}
	</UserRoleAccess>;
};
